import apiClient from './api';

export interface YouTubeAccount {
  _id: string;
  channelId: string;
  channelTitle: string;
  channelDescription?: string;
  channelThumbnailUrl?: string;
  isActive: boolean;
  needsReconnect?: boolean;
  lastAuthError?: string;
  lastAuthErrorAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type YouTubePublishJobStatus = 'pending' | 'scheduled' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface YouTubePublishJobResult {
  jobId: string;
  status: YouTubePublishJobStatus;
  videoId?: string;
  videoUrl?: string;
  error?: string;
  scheduledFor?: string;
  createdAt: string;
  updatedAt: string;
}

export interface YouTubeUpload {
  _id: string;
  videoId: string;
  title: string;
  description?: string;
  privacyStatus: 'private' | 'unlisted' | 'public';
  publishedAt: string;
  videoUrl: string;
  channelTitle?: string;
  channelId?: string;
}

export interface YouTubePublishRequest {
  sourceType: 'translation' | 'upload' | 'clip';
  sourceId: string;
  accountId: string;
  title: string;
  description?: string;
  tags?: string[];
  privacyStatus: 'private' | 'unlisted' | 'public';
  categoryId?: string;
  thumbnailUrl?: string;
  language?: string;
  publishMode?: 'new' | 'overwrite';
  scheduledFor?: string;
  selfDeclaredMadeForKids: boolean;
  playlistId?: string;
  license?: 'youtube' | 'creativeCommon';
  embeddable?: boolean;
  publicStatsViewable?: boolean;
  captionsSrtUrl?: string;
  useOrgBranding?: boolean;
  applyFadeOut?: boolean;
}

export interface YouTubePlaylist {
  id: string;
  title: string;
}

export interface YouTubeConfig {
  enabled: boolean;
  globallyConfigured: boolean;
}

export const youtubeApi = {
  getConfig: async (): Promise<{ success: boolean; enabled: boolean; globallyConfigured: boolean }> => {
    const response = await apiClient.get('/youtube/config');
    return response.data;
  },

  updateConfig: async (enabled: boolean): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.put('/youtube/config', { enabled });
    return response.data;
  },

  getAuthUrl: async (): Promise<{ success: boolean; url: string; state: string }> => {
    const response = await apiClient.get('/youtube/auth/url');
    return response.data;
  },

  getAccounts: async (): Promise<{ success: boolean; accounts: YouTubeAccount[] }> => {
    const response = await apiClient.get('/youtube/accounts');
    return response.data;
  },

  unlinkAccount: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.delete(`/youtube/accounts/${id}`);
    return response.data;
  },

  publish: async (payload: YouTubePublishRequest): Promise<{ success: boolean; message?: string; publishJobId?: string; scheduledFor?: string }> => {
    const response = await apiClient.post('/youtube/publish', payload);
    return response.data;
  },

  pollPublishJob: async (publishJobId: string): Promise<{ success: boolean; job: YouTubePublishJobResult }> => {
    const response = await apiClient.get(`/youtube/publish-jobs/${publishJobId}`);
    return response.data;
  },

  reschedulePublishJob: async (publishJobId: string, scheduledFor: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.patch(`/youtube/publish-jobs/${publishJobId}`, { scheduledFor });
    return response.data;
  },

  cancelPublishJob: async (publishJobId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.patch(`/youtube/publish-jobs/${publishJobId}`, { cancel: true });
    return response.data;
  },

  getUploads: async (): Promise<{ success: boolean; uploads: YouTubeUpload[] }> => {
    const response = await apiClient.get('/youtube/uploads');
    return response.data;
  },

  getPlaylists: async (accountId: string): Promise<{ success: boolean; playlists: YouTubePlaylist[] }> => {
    const response = await apiClient.get(`/youtube/accounts/${accountId}/playlists`);
    return response.data;
  },
};
